import type { FC } from 'react';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  ButtonGroup,
} from '@material-ui/core';
import { DateRange } from '@material-ui/lab';
import { useQuery } from 'react-query';
import { Query } from '../../../../components/query';
import { DateRangeField } from '../../../../components/date-range-field';
import { JobsBadges } from '../../../../components/job/jobs-badges';
import { FilterOperator } from '../../../../utils/filter-operators';
import { Job } from '../../../../types/job';
import { useAuth } from '../../../../hooks/use-auth';
import { useAxios } from '../../../../hooks/use-axios';
import { ResponseData, ListResponse } from '../../../../types/axios';
import { AutocompleteField } from '../../../../components/autocomplete-field';
import { SKU } from '../../../../types/sku';
import { User } from '../../../../types/user';
import { FilterValue, ELastDays, EDateFilter } from '../../../../types/filter';
import { EJobsView } from '../../../../utils/jobs-helper';
import { MAX_PAGE_SIZE } from '../../../../utils/utils';
import { InputField } from '../../../../components/input-field';
import { useDebounce } from '../../../../hooks/use-debounce';

interface JobsFilterProps {
  disabled: boolean;
  onRangeChange: (
    dateFilterType: EDateFilter,
    newRange: DateRange<Date>,
    lastDays?: number,
  ) => void;
  onQueryChange: (newQuery: string) => void;
  onViewChange: (newView: EJobsView) => void;
  onFilterChange: (newFilterValue: FilterValue) => void;
  dateFilterType: EDateFilter;
  lastDays?: ELastDays | number;
  range: DateRange<Date>;
  query: string;
  view: EJobsView;
  filters?: FilterValue[];
}

interface View {
  label: string;
  value: EJobsView;
}

enum FilterProperty {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  SKU = 'item.sku',
  USER = 'user.email',
}

enum EDateFilterValue {
  TODAY = 'today',
  WEEK = 'week',
  MONTH = 'month',
  XDAYS = 'xdays',
  RANGE = 'range',
}

// Keep vera-admin views
const views: View[] = [
  {
    label: 'Processed',
    value: EJobsView.ALL,
  },
  {
    label: 'Protected',
    value: EJobsView.PROTECTED,
  },
  {
    label: 'Unable To Protect',
    value: EJobsView.UNABLE_TO_PROTECT,
  },
  {
    label: 'Verified',
    value: EJobsView.VERIFIED,
  },
  {
    label: 'Unable To Verify',
    value: EJobsView.UNABLE_TO_VERIFY,
  },
  {
    label: 'Not Verified',
    value: EJobsView.NOT_VERIFIED,
  },
];

const getNow = (startOfDay: boolean = false, addDays: number = 0): Date => {
  const now = new Date();
  now.setHours(23, 59, 59);
  if (startOfDay) {
    now.setHours(0, 0, 0, 0);
  }
  return new Date(now.setDate(now.getDate() + addDays));
};

const getTodayRange = (): DateRange<Date> => [getNow(true), getNow()];
const getWeekRange = (): DateRange<Date> => [getNow(true, -6), getNow()];
const getMonthRange = (): DateRange<Date> => [getNow(true, -30), getNow()];
const getLastDaysRange = (days: number): DateRange<Date> => [getNow(true, -(days - 1)), getNow()];

export const getDefaultRange = getWeekRange;

const getActiveFilter = (
  dateFilterType: EDateFilter,
  lastDays?: ELastDays | number,
): EDateFilterValue => {
  if (dateFilterType === EDateFilter.RANGE) {
    return EDateFilterValue.RANGE;
  }
  switch (lastDays) {
    case ELastDays.TODAY:
      return EDateFilterValue.TODAY;
    case ELastDays.WEEK:
      return EDateFilterValue.WEEK;
    case ELastDays.MONTH:
      return EDateFilterValue.MONTH;
    default:
      return EDateFilterValue.XDAYS;
  }
};

export const JobsFilter: FC<JobsFilterProps> = (props) => {
  const {
    disabled,
    onRangeChange,
    onQueryChange,
    onViewChange,
    onFilterChange,
    range,
    view,
    query,
    filters,
    dateFilterType,
    lastDays,
  } = props;
  const [lastDaysInput, setLastDaysInput] = useState(lastDays ?? '');
  const debouncedLastDays = useDebounce(lastDaysInput, 400);

  const { axios } = useAxios();
  const { tenant } = useAuth();

  // Count query (optional, can be removed if not needed)
  const { data: allCount, isLoading: isAllCountLoading } = useQuery(
    ['jobs-all', tenant, range],
    async () => {
      const url = '/jobs';
      const {
        data: {
          data: { recordsFiltered },
        },
      } = await axios.get<ResponseData<ListResponse<Job>>>(url, {
        params: {
          start: 0,
          length: 0,
          'globalFilter[tenant]': tenant?.id,
          'globalFilter[createdAt]': {
            operator: FilterOperator.BETWEEN,
            value: [range[0].toISOString(), new Date(range[1].setHours(23, 59, 59)).toISOString()],
          },
          onlyCount: true,
        },
      });
      return recordsFiltered;
    },
  );

  const {
    data: skus,
    isLoading: isSkusLoading,
    error: skusError,
  } = useQuery(
    ['jobs-skus', tenant],
    async () => {
      const url = '/skus';
      const {
        data: {
          data: { data },
        },
      } = await axios.get<ResponseData<ListResponse<SKU>>>(url, {
        params: {
          start: 0,
          length: MAX_PAGE_SIZE,
          'globalFilter[tenant]': tenant?.id,
        },
      });
      return data;
    },
    {
      initialData: [] as SKU[],
      refetchOnWindowFocus: false,
    },
  );

  const {
    data: users,
    isLoading: isUsersLoading,
    error: usersError,
  } = useQuery(
    ['jobs-users', tenant],
    async () => {
      const url = '/users';
      const {
        data: {
          data: { data },
        },
      } = await axios.get<ResponseData<ListResponse<User>>>(url, {
        params: {
          start: 0,
          length: MAX_PAGE_SIZE,
          tenantId: tenant?.id,
        },
      });
      return data;
    },
    {
      initialData: [] as User[],
      refetchOnWindowFocus: false,
    },
  );

  const handleTodayClick = () => {
    onRangeChange(EDateFilter.LAST_DAYS, getTodayRange(), ELastDays.TODAY);
  };

  const handleWeekClick = () => {
    onRangeChange(EDateFilter.LAST_DAYS, getWeekRange(), ELastDays.WEEK);
  };

  const handleMonthClick = () => {
    onRangeChange(EDateFilter.LAST_DAYS, getMonthRange(), ELastDays.MONTH);
  };

  const handleLastDaysClick = () => {
    onRangeChange(
      EDateFilter.LAST_DAYS,
      getLastDaysRange(Number(lastDaysInput)),
      ELastDays.CUSTOM,
    );
  };

  const handleLastDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLastDaysInput(e.target.value);
  };

  useEffect(() => {
    if (
      !Number.isNaN(debouncedLastDays) &&
      debouncedLastDays !== '' &&
      Number(debouncedLastDays) >= 0 &&
      debouncedLastDays !== lastDays
    ) {
      onRangeChange(
        EDateFilter.LAST_DAYS,
        getLastDaysRange(Number(debouncedLastDays)),
        Number(debouncedLastDays),
      );
    }
  }, [debouncedLastDays]);

  useEffect(() => {
    setLastDaysInput(lastDays ?? '');
  }, [lastDays]);

  const handleSkuChange = (_e: any, value?: SKU) => {
    onFilterChange({
      property: FilterProperty.SKU,
      value: value?.id ?? null,
      operator: FilterOperator.EQUAL,
    });
  };

  const handleUserChange = (_e: any, value?: string | User) => {
    onFilterChange({
      property: FilterProperty.USER,
      value: (typeof value === 'string' ? value : value?.email) ?? null,
      operator: FilterOperator.CONTAINS,
    });
  };

  return (
    <>
      <CardHeader
        title={
          <Box
            sx={{
              display: 'flex',
              gridGap: '0.5em',
              mr: 1,
            }}
          >
            {isAllCountLoading ? (
              <Skeleton variant="text" width={30} />
            ) : (
              <Chip
                size="small"
                variant="filled"
                label={allCount}
                sx={{ borderRadius: 16, color: 'primary.contrastText', background: '#506176' }}
              />
            )}
            <Typography>{allCount !== 1 ? 'Events' : 'Event'}</Typography>
          </Box>
        }
        action={
          <Box
            sx={{
              display: 'flex',
              gridGap: '1em',
            }}
          >
            <ButtonGroup variant="outlined" color="primary">
              <Button
                onClick={handleTodayClick}
                variant={
                  getActiveFilter(dateFilterType, lastDays) === EDateFilterValue.TODAY
                    ? 'contained'
                    : 'outlined'
                }
              >
                Today
              </Button>
              <Button
                onClick={handleWeekClick}
                variant={
                  getActiveFilter(dateFilterType, lastDays) === EDateFilterValue.WEEK
                    ? 'contained'
                    : 'outlined'
                }
              >
                Last 7 days
              </Button>
              <Button
                onClick={handleMonthClick}
                variant={
                  getActiveFilter(dateFilterType, lastDays) === EDateFilterValue.MONTH
                    ? 'contained'
                    : 'outlined'
                }
              >
                Last 30 days
              </Button>
              <Button
                onClick={handleLastDaysClick}
                variant={
                  getActiveFilter(dateFilterType, lastDays) === EDateFilterValue.XDAYS
                    ? 'contained'
                    : 'outlined'
                }
              >
                Last X days
              </Button>
              <Button
                onClick={() => onRangeChange(EDateFilter.RANGE, range, lastDays)}
                variant={
                  getActiveFilter(dateFilterType, lastDays) === EDateFilterValue.RANGE
                    ? 'contained'
                    : 'outlined'
                }
              >
                Custom Range
              </Button>
            </ButtonGroup>
            <InputField
              type="number"
              size="small"
              value={lastDaysInput}
              onChange={handleLastDaysChange}
              inputProps={{ min: 0, style: { width: 45, padding: '6px 8px' } }}
              sx={{ alignSelf: 'center' }}
              disabled={getActiveFilter(dateFilterType, lastDays) === EDateFilterValue.RANGE}
            />
            <DateRangeField
              fullWidth
              onChange={() => {}}
              onAccept={(newRange) => onRangeChange(EDateFilter.RANGE, newRange)}
              value={range}
              disabled={getActiveFilter(dateFilterType, lastDays) !== EDateFilterValue.RANGE}
            />
          </Box>
        }
      />
      <Divider />
      <CardContent>
        <JobsBadges range={range} />
      </CardContent>
      <Divider />
      <Box
        sx={{
          px: {
            sm: 3,
          },
        }}
      >
        <Tabs
          onChange={(_event, value) => onViewChange?.(value)}
          allowScrollButtonsMobile
          value={view}
          variant="scrollable"
        >
          {views.map((option) => (
            <Tab disabled={disabled} key={option.label} label={option.label} value={option.value} />
          ))}
        </Tabs>
      </Box>
      <Divider />
      <Box
        sx={{
          alignItems: 'center',
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            sm: '1fr auto auto',
            xs: 'auto',
          },
          justifyItems: 'flex-start',
          p: 3,
        }}
      >
        <Query disabled={disabled} onChange={onQueryChange} value={query} />
        <AutocompleteField
          label=""
          options={skus || []}
          getOptionLabel={(option) => option.title || `[${option.id}]`}
          error={Boolean(skusError)}
          filterSelectedOptions
          clearOnEscape
          loading={isSkusLoading}
          onChange={handleSkuChange}
          placeholder="Select SKU"
          value={
            skus?.find(
              (sku) => sku.id === filters?.find((f) => f.property === FilterProperty.SKU)?.value,
            ) || null
          }
          sx={{
            width: 300,
            '& input': {
              height: '30px !important',
              fontSize: '1em !important',
            },
          }}
        />
        <AutocompleteField
          label=""
          options={users || []}
          getOptionLabel={(option) => option.email || option}
          error={Boolean(usersError)}
          filterSelectedOptions
          clearOnEscape
          loading={isUsersLoading}
          onChange={handleUserChange}
          placeholder="Search user"
          value={
            users?.find(
              (user) =>
                user.email === filters?.find((f) => f.property === FilterProperty.USER)?.value,
            ) ||
            filters?.find((f) => f.property === FilterProperty.USER)?.value ||
            null
          }
          freeSolo
          sx={{
            width: 300,
            '& input': {
              height: '30px !important',
              fontSize: '1em !important',
            },
          }}
        />
      </Box>
    </>
  );
};

JobsFilter.propTypes = {
  disabled: PropTypes.bool,
  onRangeChange: PropTypes.func,
  onQueryChange: PropTypes.func,
  onViewChange: PropTypes.func,
  onFilterChange: PropTypes.func,
  dateFilterType: PropTypes.any,
  lastDays: PropTypes.any,
  range: PropTypes.any,
  query: PropTypes.string,
  view: PropTypes.any,
  filters: PropTypes.any,
};
